import { useEffect, useMemo, useState } from "react";
import { vocabularize } from "src/Common/vocab";
import { useNavigate, useParams } from "react-router-dom";
import moment from "moment";
import { getImageUrl } from "src/Common/common";
import { can, handleError } from "src/Common/function";
import PolicyService from "src/Services/API/policy.service";
import Loader from "src/Components/Common/Loader";
import AssignPolicyToRolesModal from "./AssignPolicyToRolesModal";
import PolicyWizardModal from "./PolicyWizardModal";

function StatCard({ label, value, icon, iconBg }) {
  return (
    <div className="flex-1 flex items-center justify-between gap-2 bg-white rounded-[12px] outline outline-1 -outline-offset-1 outline-[#E5E8F3] px-5 py-6">
      <div className="flex flex-col items-start gap-0.5 overflow-hidden">
        <p className="text-[14px] leading-5 tracking-[0.14px] font-OpenSansSemiBold text-[#7C829A] capitalize">
          {label}
        </p>
        <p className="text-[24px] leading-8 font-OpenSansSemiBold text-[#1A2332] capitalize">{value}</p>
      </div>
      {icon && (
        <div
          className="w-10 h-10 shrink-0 flex items-center justify-center rounded-[11.43px]"
          style={{ background: iconBg }}
        >
          {icon}
        </div>
      )}
    </div>
  );
}

/**
 * Policy detail — a read-only view (stats cards, tabs, permission table).
 * Editing happens in the 3-step Policy wizard modal, which "Edit Policy" opens.
 */
function PolicyDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [policy, setPolicy] = useState(null);
  const [tab, setTab] = useState("permissions");
  const [assignShown, setAssignShown] = useState(false);
  const [editShown, setEditShown] = useState(false);

  const load = () => {
    PolicyService.getOne(id)
      .then((res) => setPolicy(res.data))
      .catch(handleError);
  };

  useEffect(load, [id]);

  const rows = useMemo(() => policy?.permissions ?? [], [policy]);

  // Flat permission list for the Permissions table, kept in catalog order
  // (by `resourceOrder`, then display name) so related permissions stay together.
  const permissionRows = useMemo(
    () =>
      [...rows].sort(
        (a, b) =>
          (a.resourceOrder ?? Number.MAX_SAFE_INTEGER) - (b.resourceOrder ?? Number.MAX_SAFE_INTEGER) ||
          (a.displayName || a.key).localeCompare(b.displayName || b.key),
      ),
    [rows],
  );

  if (!policy) {
    return (
      <div className="min-h-screen bg-[#FBFCFF] px-7 py-10">
        <Loader variant="block" ariaLabel="loading-policy" />
      </div>
    );
  }

  const roles = policy.roles ?? [];

  return (
    <div className="min-h-screen bg-[#FBFCFF] px-7 py-6">
      {/* History-based back: this page is reached from the policy list AND
          from role detail, so a hardcoded /policies would break the latter. */}
      <div className="flex items-center cursor-pointer mb-4" onClick={() => navigate(-1)}>
        <img src={getImageUrl("/images/common/back_arrow.png")} alt="" className="w-[24px]" />
        <span className="font-OpenSansRegular text-[14px] font-[700] text-subTitleColor">back</span>
      </div>

      {/* Header */}
      <div className="flex items-start justify-between mb-8 gap-4">
        <div className="flex flex-col items-start gap-1">
          <div className="flex items-center gap-3">
            <h1 className="text-[24px] leading-8 font-OpenSansSemiBold text-[#1A2332] capitalize">{vocabularize(policy.name)}</h1>
            <span className="inline-flex px-2 py-[2px] rounded-[6px] bg-[#EEF0F9] text-[#5F6477] text-[12px] leading-[18px] tracking-[0.12px] font-OpenSansSemiBold capitalize">
              {policy.type}
            </span>
          </div>
          <p className="text-[16px] leading-6 font-OpenSansRegular text-[#7C829A]">{vocabularize(policy.description) || "—"}</p>
          <div className="pt-2 flex items-center gap-1.5">
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="12"
              height="12"
              viewBox="0 0 12 12"
              fill="none"
              className="shrink-0"
            >
              <path
                d="M11 6C11 3.23858 8.76142 1 6 1C3.23858 1 1 3.23858 1 6C1 8.76142 3.23858 11 6 11C8.76142 11 11 8.76142 11 6ZM11.75 6C11.75 9.17564 9.17564 11.75 6 11.75C2.82436 11.75 0.25 9.17564 0.25 6C0.25 2.82436 2.82436 0.25 6 0.25C9.17564 0.25 11.75 2.82436 11.75 6Z"
                fill="#7C829A"
              />
              <path
                d="M5.5 2.75C5.5 2.47386 5.72386 2.25 6 2.25C6.27614 2.25 6.5 2.47386 6.5 2.75V5.66463L8.47759 6.65342C8.68926 6.75925 8.77515 7.01709 8.66932 7.22876C8.56349 7.44043 8.30565 7.52632 8.09398 7.42049L5.77639 6.2617C5.60693 6.17697 5.5 6.00374 5.5 5.81426V2.75Z"
                fill="#7C829A"
              />
            </svg>
            <span className="text-[12px] leading-[18px] tracking-[0.12px] font-OpenSansRegular text-[#7C829A]">
              Last updated: {policy.updatedAt ? moment(policy.updatedAt).fromNow() : "—"}
              {policy.updatedBy?.name ? ` by ${policy.updatedBy.name}` : ""}
            </span>
          </div>
        </div>
        <div className="flex items-center gap-3">
          {can("iam:updatePolicy") && (
            <button
              onClick={() => setEditShown(true)}
              className="h-8 px-3 rounded-[8px] bg-white shadow-[0px_1px_2px_rgba(16,24,40,0.05)] outline outline-1 -outline-offset-1 outline-[#C9CEE2] text-[14px] leading-5 tracking-[0.14px] capitalize font-OpenSansSemiBold text-[#1A2332] hover:bg-[#F8F9FE] inline-flex items-center justify-center gap-2"
            >
              <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 16 16" fill="none">
                <path
                  d="M11.333 2.5a1.414 1.414 0 012 2L5 12.833l-2.667.667.667-2.667L11.333 2.5z"
                  stroke="#1A2332"
                  strokeWidth="1.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
              Edit Policy
            </button>
          )}
          {can("iam:updatePolicy") && (
            <button
              onClick={() => setAssignShown(true)}
              className="h-8 px-3 rounded-[8px] bg-[#0065FD] text-white text-[14px] leading-5 tracking-[0.14px] capitalize font-OpenSansSemiBold hover:bg-[#0048bd] inline-flex items-center justify-center gap-2"
            >
              <svg xmlns="http://www.w3.org/2000/svg" width="15" height="13" viewBox="0 0 15 13" fill="none">
                <path
                  d="M10.6667 12.5C10.6667 11.2181 10.1577 9.98846 9.2513 9.08203C8.34488 8.17561 7.11521 7.66667 5.83333 7.66667C4.55145 7.66667 3.32179 8.17561 2.41536 9.08203C1.50894 9.98846 1 11.2181 1 12.5C1 12.7761 0.776142 13 0.5 13C0.223858 13 0 12.7761 0 12.5C0 10.9529 0.614371 9.46896 1.70833 8.375C2.8023 7.28104 4.28624 6.66667 5.83333 6.66667C7.38043 6.66667 8.86437 7.28104 9.95833 8.375C11.0523 9.46896 11.6667 10.9529 11.6667 12.5C11.6667 12.7761 11.4428 13 11.1667 13C10.8905 13 10.6667 12.7761 10.6667 12.5Z"
                  fill="white"
                />
                <path
                  d="M8.66667 3.83333C8.66667 2.26853 7.39814 1 5.83333 1C4.26853 1 3 2.26853 3 3.83333C3 5.39814 4.26853 6.66667 5.83333 6.66667C7.39814 6.66667 8.66667 5.39814 8.66667 3.83333ZM9.66667 3.83333C9.66667 5.95043 7.95042 7.66667 5.83333 7.66667C3.71624 7.66667 2 5.95043 2 3.83333C2 1.71624 3.71624 0 5.83333 0C7.95042 0 9.66667 1.71624 9.66667 3.83333Z"
                  fill="white"
                />
                <path
                  d="M13.3335 11.8332C13.3334 9.76626 12.0929 7.82004 10.8667 6.90026C10.7408 6.80584 10.6668 6.65723 10.6668 6.49987C10.6668 6.34257 10.7408 6.19454 10.8667 6.10013C11.2391 5.82069 11.537 5.45372 11.7339 5.03177C11.9307 4.60971 12.0208 4.14525 11.9956 3.68021C11.9704 3.21518 11.8307 2.76339 11.5893 2.3651C11.348 1.96687 11.012 1.63456 10.6115 1.39701C10.374 1.25614 10.2955 0.948965 10.4363 0.711458C10.5772 0.473956 10.8844 0.395477 11.1219 0.536328C11.6638 0.857761 12.1182 1.308 12.4448 1.84688C12.7713 2.38575 12.9602 2.99699 12.9943 3.62617C13.0284 4.25533 12.9065 4.88329 12.6401 5.4543C12.4614 5.8374 12.2195 6.18536 11.9285 6.48815C13.1966 7.66254 14.3334 9.66802 14.3335 11.8332C14.3335 12.1093 14.1095 12.3331 13.8335 12.3332C13.5573 12.3332 13.3335 12.1093 13.3335 11.8332Z"
                  fill="white"
                />
              </svg>
              Assign Policy
            </button>
          )}
        </div>
      </div>

      {/* Stats cards */}
      <div className="flex gap-4 mb-6">
        <StatCard
          label="Total Permissions"
          value={rows.length}
          iconBg="#DAEBFF"
          icon={
            <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 20 20" fill="none">
              <path
                d="M5.833 2.5h8.334c.92 0 1.666.746 1.666 1.667v11.666c0 .92-.746 1.667-1.666 1.667H5.833c-.92 0-1.666-.746-1.666-1.667V4.167c0-.921.746-1.667 1.666-1.667zM7.5 7.5h5M7.5 10.833h5M7.5 14.167h2.5"
                stroke="#0058E5"
                strokeWidth="1.5"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          }
        />
        <StatCard
          label="Attached Roles"
          value={roles.length}
          iconBg="#FFEDD5"
          icon={
            <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 20 20" fill="none">
              <path
                d="M10 2.5l6.667 2.917v3.75c0 3.906-2.625 7.56-6.667 8.75-4.042-1.19-6.667-4.844-6.667-8.75v-3.75L10 2.5z"
                stroke="#C2410C"
                strokeWidth="1.5"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          }
        />
        <StatCard
          label="Assigned Users"
          value={policy.assignedUserCount ?? 0}
          iconBg="#F3E8FF"
          icon={
            <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 20 20" fill="none">
              <path
                d="M13.333 17.5v-1.667a3.333 3.333 0 00-3.333-3.333H4.167a3.333 3.333 0 00-3.334 3.333V17.5M7.083 9.167a3.333 3.333 0 100-6.667 3.333 3.333 0 000 6.667zM19.167 17.5v-1.667a3.333 3.333 0 00-2.5-3.225M13.333 2.608a3.333 3.333 0 010 6.459"
                stroke="#7E22CE"
                strokeWidth="1.5"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          }
        />
      </div>

      {/* Tabs */}
      <div className="flex items-center justify-between border-b border-[#E5E8F3] mb-6">
        <div className="flex gap-6">
          {[
            ["permissions", "Permissions"],
            ["roles", `Attached Roles (${roles.length})`],
            ["users", `Assigned Users (${policy.assignedUserCount ?? 0})`],
          ].map(([key, label]) => (
            <button
              key={key}
              onClick={() => setTab(key)}
              className={`py-4 text-[14px] leading-5 tracking-[0.14px] capitalize font-OpenSansSemiBold border-b-2 -mb-[1px] ${
                tab === key
                  ? "text-[#0058E5] border-[#0058E5]"
                  : "text-[#7C829A] border-transparent hover:text-[#1A2332]"
              }`}
            >
              {label}
            </button>
          ))}
        </div>
      </div>

      {/* Permissions tab — flat table (Permission / Description) */}
      {tab === "permissions" &&
        (permissionRows.length === 0 ? (
          <div className="bg-white rounded-[12px] outline outline-1 -outline-offset-1 outline-[#E5E8F3] px-6 py-10 text-center text-[#868DA6] font-OpenSansRegular">
            No permissions in this policy.
          </div>
        ) : (
          <div className="bg-white rounded-[12px] outline outline-1 -outline-offset-1 outline-[#E5E8F3] shadow-[0px_1px_2px_rgba(0,0,0,0.06)] overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full border-collapse">
                <thead>
                  <tr className="bg-[#F8F9FE] border-b border-[#E5E8F3]">
                    <th className="w-1/2 text-left px-6 h-11 text-[12px] leading-[18px] font-OpenSansSemiBold text-[#5F6477]">
                      Permission
                    </th>
                    <th className="w-1/2 text-left px-6 h-11 text-[12px] leading-[18px] font-OpenSansSemiBold text-[#5F6477]">
                      Description
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {permissionRows.map((perm) => (
                    <tr
                      key={perm.key}
                      className="h-[72px] border-b border-[#E5E8F3] last:border-b-0 hover:bg-[#F8F9FE]"
                    >
                      <td className="px-6 py-4 align-middle">
                        <span className="text-[14px] leading-5 font-OpenSansSemiBold text-[#1A2332]">
                          {perm.displayName || perm.key}
                        </span>
                      </td>
                      <td className="px-6 py-4 align-middle">
                        <span className="text-[12px] leading-[18px] tracking-[0.12px] font-OpenSansRegular text-[#5F6477]">
                          {perm.description || "—"}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        ))}

      {/* Attached Roles tab */}
      {tab === "roles" && (
        <div className="bg-white rounded-[12px] border border-[#E5E8F3] overflow-hidden">
          {roles.length === 0 ? (
            <div className="px-6 py-10 text-center text-[#868DA6] font-OpenSansRegular">Not attached to any role.</div>
          ) : (
            roles.map((r) => (
              <div
                key={r.id}
                onClick={() => navigate(`/roles/${r.id}`)}
                className="flex items-center justify-between px-6 py-3 border-b border-[#E5E8F3] last:border-b-0 cursor-pointer hover:bg-[#F8F9FE]"
              >
                <span className="text-[14px] font-OpenSansSemiBold text-[#1A2332] capitalize">{r.name}</span>
                <span className="text-[13px] font-OpenSansRegular text-[#868DA6]">
                  {r.userCount} {r.userCount === 1 ? "user" : "users"}
                </span>
              </div>
            ))
          )}
        </div>
      )}

      {/* Assigned Users tab — users this policy is attached to directly
          (users granted via roles are not listed here) */}
      {tab === "users" && (
        <div className="bg-white rounded-[12px] border border-[#E5E8F3] overflow-hidden">
          {(policy.users ?? []).length === 0 ? (
            <div className="px-6 py-10 text-center text-[#868DA6] font-OpenSansRegular">
              Not assigned directly to any user.
            </div>
          ) : (
            (policy.users ?? []).map((u) => (
              <div
                key={u.id}
                className="flex items-center justify-between px-6 py-3 border-b border-[#E5E8F3] last:border-b-0"
              >
                <div className="flex flex-col gap-0.5 min-w-0">
                  <span className="text-[14px] font-OpenSansSemiBold text-[#1A2332]">{u.name}</span>
                  {/* Branch scope of Manage Time Off Requests for this
                      attachment (set in the member's Edit drawer). */}
                  {policy.branchScoped && (
                    <span className="text-[12px] leading-[18px] font-OpenSansRegular text-[#7C829A]">
                      Time off branches:{" "}
                      {u.branchScope == null
                        ? "All branches"
                        : u.branchScope.length
                          ? u.branchScope.map((b) => b.name).join(", ")
                          : "None"}
                    </span>
                  )}
                </div>
                <span className="text-[13px] font-OpenSansRegular text-[#868DA6]">{u.email}</span>
              </div>
            ))
          )}
        </div>
      )}

      <AssignPolicyToRolesModal
        shown={assignShown}
        policyId={Number(id)}
        attachedRoleIds={roles.map((r) => r.id)}
        attachedUserIds={(policy.users ?? []).map((u) => u.id)}
        onClose={() => setAssignShown(false)}
        onSaved={load}
      />
      <PolicyWizardModal shown={editShown} policyId={Number(id)} onClose={() => setEditShown(false)} onSaved={load} />
    </div>
  );
}

export default PolicyDetail;
